import {
  ApiResponse,
  GetPropertiesByRealtorParams,
  HomepagePropertiesQuery,
  PropertyStatus,
  UserPropertyMetricsResponse,
} from "@/types";
import {
  AddPropertyData,
  AddPropertyPayload,
  EditPropertyPayload,
  GetSavedPropertiesResponse,
  PaginatedResponse,
  Property,
} from "@/types/property";

import { axiosWithToken } from "@/utils/axios";
import { AxiosResponse } from "axios";
import {
  buildActionId,
  getActionIdempotencyKey,
  idempotencyHeaders,
} from "@/utils/idempotency";

export enum PropertyLeadType {
  WHATSAPP_CLICK = 'whatsapp_click',
  DIRECT_CALL_CLICK = 'direct_call_click',
}

export interface CreatePropertyLeadPayload {
  propertyId: string;
  type: PropertyLeadType;
  leadUserId?: string | null;
  isGuestUser?: boolean;
  realtorId?: string | null;
  propertyCreatedById?: string | null;
  leadUserRole?: string | null;
  userAgent?: string | null;
  ipAddress?: string | null;
}

/**
 * Idempotency options for writes that must be applied at most once per user
 * action (see `utils/idempotency.ts`).
 */
export interface IdempotentRequestOptions {
  /**
   * Key minted when the user started the action (`startUserAction`). When
   * omitted, a key is derived from the action + property and memoised for the
   * dedupe window, so rapid duplicate calls still collapse into one write.
   */
  idempotencyKey?: string;
}


class ServiceClass {
  AddProperty(payload: Property) {
    return axiosWithToken().post("/properties", {
      ...payload,
    });
  }

  AddPropertyV2(
    payload: AddPropertyPayload,
  ): Promise<AxiosResponse<ApiResponse<AddPropertyData>>> {
    return axiosWithToken().post("/properties", payload);
  }

  UpdatePropertyStatus(payload: {
    id: string;
    status: PropertyStatus;
  }): Promise<AxiosResponse<ApiResponse<any>>> {
    const { id, ...body } = payload;
    return axiosWithToken().patch(`/properties/${id}`, body);
  }

  UpdateProperty(
    payload: EditPropertyPayload & { id: string },
  ): Promise<AxiosResponse<ApiResponse<any>>> {
    const { id, ...body } = payload;
    return axiosWithToken().patch(`/properties/${id}/edit`, body);
  }

  GetUploadUrl(key: string) {
    return axiosWithToken().post("/providers/generate_upload_url", {
      key,
    });
  }

  GetProperties(payload?: any, options?: { signal?: AbortSignal }) {
    return axiosWithToken().get("/properties", {
      params: payload,
      ...(options?.signal ? { signal: options.signal } : {}),
    });
  }

  GetUserPropertyMetrics(): Promise<
    AxiosResponse<{
      status: string;
      message: string;
      data: UserPropertyMetricsResponse;
    }>
  > {
    return axiosWithToken().get("/properties/user/metrics");
  }

  GetPropertiesWithGeo(
    payload: HomepagePropertiesQuery,
    options?: { signal?: AbortSignal },
  ) {
    return axiosWithToken().get("/properties/homepage", {
      params: payload,
      ...(options?.signal ? { signal: options.signal } : {}),
    });
  }

  GetDeals(payload: HomepagePropertiesQuery) {
    return axiosWithToken().get("/properties/deals", {
      params: payload,
    });
  }

  GetManagedProperties(payload: HomepagePropertiesQuery) {
    return axiosWithToken().get("/properties/managed_properties", {
      params: payload,
    });
  }

  GetUserProperties(payload?: any): Promise<AxiosResponse<any>> {
    return axiosWithToken().get("/properties/user", {
      params: payload,
    });
  }

  GetPropertiesByRealtor(
    payload?: GetPropertiesByRealtorParams,
  ): Promise<AxiosResponse<PaginatedResponse<Property>>> {
    const { realtorId, ...queryParams } = payload || {};

    // If realtorId is optional or part of the path:
    const path = realtorId
      ? `/properties/realtor/${realtorId}`
      : `/properties/realtor`;

    return axiosWithToken().get(path, {
      params: queryParams,
    });
  }

  GetPropertyById(payload: {
    id: string;
  }): Promise<AxiosResponse<ApiResponse<Property>>> {
    return axiosWithToken().get(`/properties/${payload.id}`);
  }

  GetSimilarProperties(payload: {
    id: string;
    page?: number;
    pageSize?: number;
  }) {
    const { id, ...queryParams } = payload;
    return axiosWithToken().get(`/properties/${id}/similar`, {
      params: queryParams,
    });
  }

  SaveProperty(payload?: { propertyId: string }) {
    return axiosWithToken().post(`/properties/saved_properties`, {
      ...payload,
      // params: payload,
    });
  }

  UnsaveProperty(payload?: { propertyId: string }) {
    return axiosWithToken().delete(
      `/properties/saved_properties/${payload?.propertyId}`,
      {
        params: payload,
      },
    );
  }

  GetSavedProperties(): Promise<AxiosResponse<GetSavedPropertiesResponse>> {
    return axiosWithToken().get(`/properties/saved_properties`);
  }

  RequestLiveInspection(payload: {
    propertyId: string;
    paymentSource: string;
    transactionId?: string;
  }) {
    return axiosWithToken().post("/properties/live_inspection", {
      ...payload,
    });
  }

  GetWeeklyDeals(payload: any) {
    return axiosWithToken().get(`/properties/weekly_deals`, {
      params: payload,
    });
  }

  HasRequestedLiveInspection(id: any) {
    return axiosWithToken().get(`/properties/${id}/has_requested`);
  }

  DeleteProperty(propertyId: string, reason?: string) {
    return axiosWithToken().delete(`/properties/${propertyId}`, {
      data: reason ? { deletion_reason: reason } : undefined,
    });
  }

  /**
   * Bumps a property insight counter. The `Idempotency-Key` is sent so the
   * action can be deduped server-side once the insights endpoint honours it,
   * and so it matches the key used by the lead request of the same action.
   */
  TrackPropertyInsight(
    propertyId: string,
    action: "inspection" | "impression" | "page_opened" | "whatsapp_contact" | "direct_phone_call",
    options?: IdempotentRequestOptions,
  ) {
    const idempotencyKey =
      options?.idempotencyKey ??
      getActionIdempotencyKey(buildActionId(action, propertyId));

    return axiosWithToken().post(
      `/properties/${propertyId}/insights`,
      { action },
      { headers: idempotencyHeaders(idempotencyKey) },
    );
  }

  /**
   * Records a lead row for a WhatsApp click or direct phone call on a property
   * listing.
   *
   * The API dedupes on the `Idempotency-Key` header, so a duplicate delivery of
   * one user action (double-fired click, retried request, duplicate mount)
   * returns the already-recorded lead instead of inserting a second row.
   */
  TrackPropertyLead(
    payload: CreatePropertyLeadPayload,
    options?: IdempotentRequestOptions,
  ) {
    const idempotencyKey =
      options?.idempotencyKey ??
      getActionIdempotencyKey(
        buildActionId(payload.type, payload.propertyId),
      );

    return axiosWithToken().post("/property_leads", payload, {
      headers: idempotencyHeaders(idempotencyKey),
    });
  }
}

const PropertyService = new ServiceClass();
export default PropertyService;

/*
import {
  TAddPropertyPayload,
  TAddPropertyData,
  TApiResponse,
  TEditOwnPropertyData,
  TEditPropertyPayload,
  TGetPropertiesByRealtorParams,
  THomepageQuery,
  THasRequestedData,
  TLiveInspectionPayload,
  TPaginatedResponse,
  TProperty,
  TPropertyMetrics,
  TPropertyQueryParams,
  TSavePropertyPayload,
} from "@/types/property-response-types";
import { axiosWithToken } from "@/utils/axios";
import { AxiosResponse } from "axios";

class ServiceClass {
  AddProperty(
    payload: TAddPropertyPayload,
  ): Promise<AxiosResponse<TApiResponse<TAddPropertyData>>> {
    return axiosWithToken().post("/properties", payload);
  }

  UpdateProperty(
    payload: TEditPropertyPayload & { id: string },
  ): Promise<AxiosResponse<TApiResponse<undefined>>> {
    const { id, ...body } = payload;
    return axiosWithToken().patch(`/properties/${id}`, body);
  }

  GetUploadUrl(
    key: string,
  ): Promise<AxiosResponse<TApiResponse<{ url: string; key: string }>>> {
    return axiosWithToken().post("/providers/generate_upload_url", { key });
  }

  GetProperties(
    payload?: TPropertyQueryParams,
  ): Promise<AxiosResponse<TPaginatedResponse<TProperty>>> {
    return axiosWithToken().get("/properties", { params: payload });
  }

  GetUserPropertyMetrics(): Promise<
    AxiosResponse<TApiResponse<TPropertyMetrics>>
  > {
    return axiosWithToken().get("/properties/user/metrics");
  }

  GetPropertiesWithGeo(
    payload: THomepageQuery,
  ): Promise<AxiosResponse<TPaginatedResponse<TProperty>>> {
    return axiosWithToken().get("/properties/homepage", { params: payload });
  }

  GetUserProperties(
    payload?: TPropertyQueryParams,
  ): Promise<AxiosResponse<TPaginatedResponse<TProperty>>> {
    return axiosWithToken().get("/properties/user", { params: payload });
  }

  GetPropertiesByRealtor(
    payload?: TGetPropertiesByRealtorParams,
  ): Promise<AxiosResponse<TPaginatedResponse<TProperty>>> {
    const { realtorId, ...queryParams } = payload || {};
    const path = realtorId
      ? `/properties/realtor/${realtorId}`
      : `/properties/realtor`;
    return axiosWithToken().get(path, { params: queryParams });
  }

  GetPropertyById(
    payload: { id: string },
  ): Promise<AxiosResponse<TApiResponse<TProperty>>> {
    return axiosWithToken().get(`/properties/${payload.id}`);
  }

  SaveProperty(
    payload: TSavePropertyPayload,
  ): Promise<AxiosResponse<TApiResponse<undefined>>> {
    return axiosWithToken().post("/properties/saved_properties", payload);
  }

  UnsaveProperty(
    payload: { propertyId: string },
  ): Promise<AxiosResponse<TApiResponse<undefined>>> {
    return axiosWithToken().delete(
      `/properties/saved_properties/${payload.propertyId}`,
    );
  }

  GetSavedProperties(): Promise<
    AxiosResponse<TPaginatedResponse<TProperty>>
  > {
    return axiosWithToken().get("/properties/saved_properties");
  }

  RequestLiveInspection(
    payload: TLiveInspectionPayload,
  ): Promise<AxiosResponse<TApiResponse<undefined>>> {
    return axiosWithToken().post("/properties/live_inspection", payload);
  }

  GetWeeklyDeals(
    payload?: TPropertyQueryParams,
  ): Promise<AxiosResponse<TPaginatedResponse<TProperty>>> {
    return axiosWithToken().get("/properties/weekly_deals", {
      params: payload,
    });
  }

  HasRequestedLiveInspection(
    propertyId: string,
  ): Promise<AxiosResponse<TApiResponse<THasRequestedData>>> {
    return axiosWithToken().get(`/properties/${propertyId}/has_requested`);
  }

  DeleteProperty(
    propertyId: string,
    reason?: string,
  ): Promise<AxiosResponse<TApiResponse<undefined>>> {
    return axiosWithToken().delete(`/properties/${propertyId}`, {
      data: reason ? { deletion_reason: reason } : undefined,
    });
  }

  TrackPropertyInsight(
    propertyId: string,
    action: "inspection" | "impression" | "page_opened",
  ): Promise<AxiosResponse<TApiResponse<unknown>>> {
    return axiosWithToken().post(`/properties/${propertyId}/insights`, {
      action,
    });
  }

  EditOwnProperty(
    propertyId: string,
    payload: TEditPropertyPayload,
  ): Promise<AxiosResponse<TApiResponse<TEditOwnPropertyData>>> {
    return axiosWithToken().patch(`/properties/${propertyId}/edit`, payload);
  }

  UpdatePropertyStatus(
    propertyId: string,
    status: string,
  ): Promise<AxiosResponse<TApiResponse<undefined>>> {
    return axiosWithToken().patch(`/properties/${propertyId}`, { status });
  }

  UpdatePropertyDescription(
    propertyId: string,
    description: string,
  ): Promise<AxiosResponse<TApiResponse<undefined>>> {
    return axiosWithToken().patch(`/properties/${propertyId}/description`, {
      description,
    });
  }

  GetSitemapData(): Promise<AxiosResponse<unknown>> {
    return axiosWithToken().get("/properties/sitemap");
  }
}

const PropertyService = new ServiceClass();
export default PropertyService;

*/
